import React, { useState, useEffect } from 'react';
import { X, ArrowRight, MapPin, Calendar, Hotel, Clock, MessageCircle, Check, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = {
  INTENT: 'intent',
  LOCATION: 'location',
  DATES: 'dates',
  ROOMS: 'rooms',
  TIMELINE: 'timeline',
  PHONE: 'phone',
  COMPLETE: 'complete'
};

const QualificationAssistant = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.INTENT);
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const [responses, setResponses] = useState({
    isSearching: null,
    city: '',
    locality: '',
    hasDate: null,
    weddingDate: '',
    roomCount: '',
    timeline: '',
    phone: '',
    timestamp: new Date().toISOString(),
    sessionId: sessionId,
    sourcePage: window.location.pathname
  });

  // Check if user has already completed qualification
  useEffect(() => {
    const hasCompleted = localStorage.getItem('wvc_qualification_completed');
    const hasSkipped = localStorage.getItem('wvc_qualification_skipped');
    if (hasCompleted || hasSkipped) {
      setHasInteracted(true);
      onComplete && onComplete();
    }
  }, [onComplete]);

  // Save partial data on each step with retry mechanism
  const savePartialData = async (data, retryCount = 0) => {
    const maxRetries = 3;
    try {
      const payload = {
        ...responses,
        ...data,
        isPartial: currentStep !== STEPS.COMPLETE && currentStep !== STEPS.PHONE,
        userAgent: navigator.userAgent
      };
      
      const response = await axios.post(`${API_URL}/leads/qualification`, payload);
      
      if (response.data.success) {
        console.log('[Qualification] Data saved successfully');
      }
      return true;
    } catch (error) {
      console.error('[Qualification] Save failed:', error.message);
      
      // Retry logic
      if (retryCount < maxRetries) {
        console.log(`[Qualification] Retrying... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return savePartialData(data, retryCount + 1);
      }
      
      // Store locally as fallback
      try {
        const failedSubmissions = JSON.parse(localStorage.getItem('wvc_failed_submissions') || '[]');
        failedSubmissions.push({
          data: { ...responses, ...data },
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('wvc_failed_submissions', JSON.stringify(failedSubmissions));
        console.log('[Qualification] Saved locally as fallback');
      } catch (storageError) {
        console.error('[Qualification] Local storage failed:', storageError);
      }
      return false;
    }
  };

  const handleIntentResponse = async (isSearching) => {
    const newData = { isSearching };
    setResponses(prev => ({ ...prev, ...newData }));
    await savePartialData(newData);
    
    if (isSearching) {
      setCurrentStep(STEPS.LOCATION);
    } else {
      localStorage.setItem('wvc_qualification_skipped', 'true');
      setHasInteracted(true);
      setIsOpen(false);
      onSkip && onSkip();
    }
  };

  const handleLocationSubmit = async () => {
    if (!responses.city.trim()) return;
    await savePartialData({ city: responses.city, locality: responses.locality });
    setCurrentStep(STEPS.DATES);
  };

  const handleDateResponse = async (hasDate) => {
    const newData = { hasDate };
    setResponses(prev => ({ ...prev, ...newData }));
    await savePartialData(newData);
    
    if (hasDate) {
      // Show date picker
    } else {
      setCurrentStep(STEPS.ROOMS);
    }
  };

  const handleDateSubmit = async () => {
    await savePartialData({ weddingDate: responses.weddingDate });
    setCurrentStep(STEPS.ROOMS);
  };

  const handleRoomsSubmit = async () => {
    if (!responses.roomCount) return;
    await savePartialData({ roomCount: responses.roomCount });
    setCurrentStep(STEPS.TIMELINE);
  };

  const handleTimelineSubmit = async (timeline) => {
    const newData = { timeline };
    setResponses(prev => ({ ...prev, ...newData }));
    await savePartialData(newData);
    setCurrentStep(STEPS.PHONE);
  };

  const handlePhoneSubmit = async () => {
    const phoneNumber = responses.phone.trim();
    
    // Simple validation - allow empty (skip) or valid phone
    if (phoneNumber && phoneNumber.length < 10) {
      return; // Don't proceed if invalid phone entered
    }
    
    await savePartialData({ phone: phoneNumber });
    handleComplete();
  };

  const handlePhoneSkip = async () => {
    await savePartialData({ phone: '' });
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('wvc_qualification_completed', 'true');
    setCurrentStep(STEPS.COMPLETE);
    
    // Auto-close after showing completion message
    setTimeout(() => {
      setIsOpen(false);
      setHasInteracted(true);
      onComplete && onComplete();
    }, 2000);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (currentStep === STEPS.INTENT) {
      localStorage.setItem('wvc_qualification_skipped', 'true');
      setHasInteracted(true);
      onSkip && onSkip();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  // Don't render anything if user has completed/skipped
  if (hasInteracted && !isOpen) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          id="wvcChatFab"
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-stone-900 hover:bg-stone-800 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          aria-label="Open chat"
          data-testid="wvc-chat-fab"
        >
          <span className="text-2xl group-hover:scale-110 transition-transform">💬</span>
        </button>
      )}

      {/* Overlay */}
      {isOpen && (
        <div 
          id="wvcChatOverlay"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          data-testid="wvc-chat-overlay"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Chat Container */}
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="wvcChatHeader bg-stone-900 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-stone-900 font-serif font-bold">W</span>
                </div>
                <div>
                  <h2 className="text-white font-semibold">Ask WVC</h2>
                  <p className="text-stone-400 text-xs">Wedding Venue Assistant</p>
                </div>
              </div>
              <button
                id="wvcChatClose"
                onClick={handleClose}
                className="text-stone-400 hover:text-white p-2 hover:bg-stone-800 rounded-full transition-colors"
                aria-label="Close chat"
                data-testid="wvc-chat-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="wvcChatBody p-5 max-h-[60vh] overflow-y-auto">
              <div id="conversationContainer">
                {/* Step: Intent Check */}
                {currentStep === STEPS.INTENT && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-stone-700 leading-relaxed">
                        Are you currently searching for a wedding venue? Let&apos;s start with a few quick questions.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleIntentResponse(true)}
                        className="flex-1 bg-stone-900 hover:bg-stone-800 h-12"
                        data-testid="intent-yes"
                      >
                        Yes, I am
                      </Button>
                      <Button
                        onClick={() => handleIntentResponse(false)}
                        variant="outline"
                        className="flex-1 border-stone-300 h-12"
                        data-testid="intent-no"
                      >
                        Just browsing
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step: Location */}
                {currentStep === STEPS.LOCATION && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-stone-700">
                        <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p>Which city or area are you planning your wedding in?</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Input
                        placeholder="City (e.g., Bangalore)"
                        value={responses.city}
                        onChange={(e) => setResponses(prev => ({ ...prev, city: e.target.value }))}
                        className="h-12"
                        data-testid="location-city"
                      />
                      <Input
                        placeholder="Area/Locality (optional)"
                        value={responses.locality}
                        onChange={(e) => setResponses(prev => ({ ...prev, locality: e.target.value }))}
                        className="h-12"
                        data-testid="location-locality"
                      />
                      <Button
                        onClick={handleLocationSubmit}
                        disabled={!responses.city.trim()}
                        className="w-full bg-stone-900 hover:bg-stone-800 h-12"
                        data-testid="location-submit"
                      >
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step: Dates */}
                {currentStep === STEPS.DATES && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-stone-700">
                        <Calendar className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p>Have you finalized your wedding date?</p>
                      </div>
                    </div>
                    {responses.hasDate === null ? (
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleDateResponse(true)}
                          className="flex-1 bg-stone-900 hover:bg-stone-800 h-12"
                          data-testid="date-yes"
                        >
                          Yes
                        </Button>
                        <Button
                          onClick={() => handleDateResponse(false)}
                          variant="outline"
                          className="flex-1 border-stone-300 h-12"
                          data-testid="date-no"
                        >
                          Not yet
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Input
                          type="date"
                          value={responses.weddingDate}
                          onChange={(e) => setResponses(prev => ({ ...prev, weddingDate: e.target.value }))}
                          className="h-12"
                          data-testid="date-input"
                        />
                        <Button
                          onClick={handleDateSubmit}
                          disabled={!responses.weddingDate}
                          className="w-full bg-stone-900 hover:bg-stone-800 h-12"
                          data-testid="date-submit"
                        >
                          Continue <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step: Rooms */}
                {currentStep === STEPS.ROOMS && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-stone-700">
                        <Hotel className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p>How many guest rooms do you need?</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {['None', '1-10', '11-30', '30+'].map((option) => (
                        <Button
                          key={option}
                          onClick={() => setResponses(prev => ({ ...prev, roomCount: option }))}
                          variant={responses.roomCount === option ? 'default' : 'outline'}
                          className={`h-12 ${responses.roomCount === option ? 'bg-stone-900' : 'border-stone-300'}`}
                          data-testid={`rooms-${option.toLowerCase().replace('+', '-plus')}`}
                        >
                          {option}
                        </Button>
                      ))}
                    </div>
                    <Button
                      onClick={handleRoomsSubmit}
                      disabled={!responses.roomCount}
                      className="w-full bg-stone-900 hover:bg-stone-800 h-12"
                      data-testid="rooms-submit"
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Step: Timeline */}
                {currentStep === STEPS.TIMELINE && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-stone-700">
                        <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p>When do you plan to book your venue?</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { value: 'immediate', label: 'This week' },
                        { value: '1month', label: 'Within a month' },
                        { value: '3months', label: 'In 1-3 months' },
                        { value: 'exploring', label: 'Just exploring' }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          onClick={() => handleTimelineSubmit(option.value)}
                          variant="outline"
                          className="w-full justify-start h-12 border-stone-300 hover:bg-stone-50"
                          data-testid={`timeline-${option.value}`}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step: Phone */}
                {currentStep === STEPS.PHONE && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-stone-700">
                        <Phone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p>What&apos;s your phone number? (We&apos;ll share relevant venue deals)</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Input
                        type="tel"
                        placeholder="Enter phone number"
                        value={responses.phone}
                        onChange={(e) => setResponses(prev => ({ ...prev, phone: e.target.value }))}
                        className="h-12"
                        data-testid="phone-input"
                      />
                      <div className="flex gap-3">
                        <Button
                          onClick={handlePhoneSubmit}
                          className="flex-1 bg-stone-900 hover:bg-stone-800 h-12"
                          data-testid="phone-submit-btn"
                        >
                          Submit
                        </Button>
                        <Button
                          onClick={handlePhoneSkip}
                          variant="outline"
                          className="text-stone-500 border-stone-300 h-12"
                          data-testid="phone-skip-btn"
                        >
                          Skip
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step: Complete */}
                {currentStep === STEPS.COMPLETE && (
                  <div className="space-y-4 animate-fade-in text-center py-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="space-y-2">
                      <p className="font-semibold text-stone-900 text-lg">Great!</p>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        You can now explore venue truth reports and real visit experiences to make better decisions.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress indicator */}
            {currentStep !== STEPS.INTENT && currentStep !== STEPS.COMPLETE && (
              <div className="px-5 pb-4">
                <div className="flex gap-1.5">
                  {['location', 'dates', 'rooms', 'timeline', 'phone'].map((step, idx) => (
                    <div
                      key={step}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        Object.values(STEPS).indexOf(currentStep) > idx + 1
                          ? 'bg-[#D4AF37]'
                          : currentStep === Object.values(STEPS)[idx + 1]
                            ? 'bg-stone-400'
                            : 'bg-stone-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default QualificationAssistant;
